import React, { useState, useEffect, useCallback } from 'react';
import { Box, CircularProgress, Alert } from '@mui/material';
import AttivitaToolbar from './attivita/AttivitaToolbar';
import AttivitaCard from './attivita/AttivitaCard';
import ActivityEditModal from './attivita/ActivityEditModal';
import InvoiceYearModal from './attivita/InvoiceYearModal';
const moment = require('moment');

const API = {
    getAttivita: (idProgetto) => `/papi2/projects/getAttivita/${idProgetto}`,
    createFromActivities: () => '/papi2/invoices/create-from-activities',
};

const Attivita = ({ idProgetto }) => {
    const [attivita, setAttivita] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notice, setNotice] = useState('');

    // Selezione multipla per fattura unica
    const [selectedIds, setSelectedIds] = useState([]);

    // Modal edit/insert
    const [editOpen, setEditOpen] = useState(false);
    const [editAttivita, setEditAttivita] = useState(null);

    // Modal anno fattura
    const [yearModalOpen, setYearModalOpen] = useState(false);
    const [yearToGenerate, setYearToGenerate] = useState(moment().year());

    // Import attività da JSON
    const [importNotice, setImportNotice] = useState('');

    const handleImportAttivita = async (file) => {
        const text = await file.text();
        let data;
        try {
            data = JSON.parse(text);
        } catch {
            setImportNotice('Errore: il file non contiene JSON valido.');
            return;
        }

        if (!Array.isArray(data)) {
            setImportNotice('Errore: il file JSON deve contenere un array di attività.');
            return;
        }

        if (data.length === 0) {
            setImportNotice('Errore: il file JSON contiene un array vuoto.');
            return;
        }

        const token = localStorage.getItem('token');
        setLoading(true);
        setImportNotice('');

        try {
            const response = await fetch(`/papi2/projects/importAttivita/${idProgetto}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });

            const result = await response.json();

            if (response.ok) {
                const msg = `✅ ${result.imported} attività importate su ${result.total}.`;
                setImportNotice(msg);
                fetchAttivita();
            } else {
                const detail = result.details
                    ? `${result.error} (${result.details.imported}/${result.details.total} importate)`
                    : result.error || 'Errore durante l\'importazione';
                setImportNotice(`❌ ${detail}`);
                setLoading(false);
            }
        } catch (error) {
            console.error('Errore import:', error);
            setImportNotice('❌ Errore di rete durante l\'importazione.');
            setLoading(false);
        }
    };

    const fetchAttivita = useCallback(() => {
        setLoading(true);
        const token = localStorage.getItem('token');
        return fetch(API.getAttivita(idProgetto), {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        })
            .then((res) => res.json())
            .then((data) => {
                setAttivita(data);
                setSelectedIds([]);
                setLoading(false);
            })
            .catch((error) => {
                console.error('Errore nel recupero delle attività:', error);
                setLoading(false);
            });
    }, [idProgetto]);

    useEffect(() => {
        fetchAttivita();
    }, [fetchAttivita]);

    // Una attività è selezionabile se completata (100%) e NON ha fattura pilota associata
    const isSelectable = (att) =>
        !!att &&
        Number(att.avanzamento) === 100 &&
        !att.idfatturapilota;

    const toggleSelect = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const openEdit = (att = null) => {
        // Blocca l'editing solo se l'attivita ha una fattura PILOTA reale associata.
        // Attivita completate ma non ancora fatturate (idfatturapilota 0/null) restano editabili.
        if (att && att.idfatturapilota) return;
        setEditAttivita(att);
        setEditOpen(true);
    };
    const closeEdit = () => {
        setEditOpen(false);
        setEditAttivita(null);
    };

    const handleDeleteSuccess = () => {
        closeEdit();
        fetchAttivita();
    };

    // Genera fattura unica per le attività selezionate
    const generateInvoices = (annoFattura) => {
        if (selectedIds.length === 0) return;
        setLoading(true);
        setNotice('');
        const token = localStorage.getItem('token');
        fetch(API.createFromActivities(), {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ idAttivita: selectedIds, annoFattura }),
        })
            .then((response) => response.json().then((d) => ({ ok: response.ok, d })))
            .then(({ ok, d }) => {
                if (ok) {
                    setYearModalOpen(false);
                    fetchAttivita();
                } else {
                    setLoading(false);
                    setNotice(d.error || 'Errore durante la generazione della fattura');
                    setYearModalOpen(false);
                }
            })
            .catch((error) => {
                console.error('Errore:', error);
                setLoading(false);
                setNotice('Errore di rete durante la generazione della fattura');
                setYearModalOpen(false);
            });
    };

    const handleGeneraFattura = () => {
        if (selectedIds.length === 0) return;
        const selectedActs = attivita.filter((a) => selectedIds.includes(a.idattivita));
        if (selectedActs.length === 0) return;

        const years = selectedActs.map((a) => moment(a.datachiusura).year());
        const minYear = Math.min(...years);
        const currentYear = moment().year();

        // Se tutte nell'anno corrente genero subito, altrimenti chiedo l'anno fiscale
        if (minYear >= currentYear) {
            generateInvoices(currentYear);
        } else {
            setYearToGenerate(minYear);
            setYearModalOpen(true);
        }
    };

    if (loading) return <Box p={2}><CircularProgress size={24} /></Box>;

    return (
        <Box sx={{ width: '100%', bgcolor: 'transparent', p: 1 }}>
            <AttivitaToolbar
                selectedCount={selectedIds.length}
                onGeneraFattura={handleGeneraFattura}
                onNuovaAttivita={() => openEdit()}
                onImportAttivita={handleImportAttivita}
            />

            {importNotice && (
                <Alert severity={importNotice.startsWith('✅') ? 'success' : 'error'} sx={{ mb: 2 }} onClose={() => setImportNotice('')}>
                    {importNotice}
                </Alert>
            )}

            {notice && (
                <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setNotice('')}>
                    {notice}
                </Alert>
            )}

            {attivita.length > 0 ? (
                <Box>
                    {attivita.map((att) => (
                        <AttivitaCard
                            key={att.idattivita}
                            attivita={att}
                            selectable={isSelectable(att)}
                            selected={selectedIds.includes(att.idattivita)}
                            onToggleSelect={toggleSelect}
                            onClick={openEdit}
                        />
                    ))}
                </Box>
            ) : (
                <Box textAlign="center" py={4} sx={{ bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 2 }}>
                    <Alert severity="info">Nessuna attività registrata per questo progetto.</Alert>
                </Box>
            )}

            <ActivityEditModal
                open={editOpen}
                idProgetto={idProgetto}
                attivita={editAttivita}
                onClose={closeEdit}
                onSaved={fetchAttivita}
                onDelete={handleDeleteSuccess}
            />

            <InvoiceYearModal
                open={yearModalOpen}
                anno={yearToGenerate}
                selectedCount={selectedIds.length}
                onChangeAnno={setYearToGenerate}
                onClose={() => setYearModalOpen(false)}
                onGenera={() => generateInvoices(yearToGenerate)}
            />
        </Box>
    );
};

export default Attivita;